import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import ForgotPasswordModal from '../components/ForgotPasswordModal';
import GoogleTranslate from '../components/common/GoogleTranslate';
import { safeJsonParse } from '../utils/storage';
import {
  normalizeEnglishDigitsInput,
  normalizeEnglishIdentifierInput,
  normalizePhoneInput,
} from '../utils/validation';
import './LoginPage.css';

const PORTALS = {
  gp: {
    eyebrow: 'नागरिक व ग्रामपंचायत सेवा',
    title: 'डिजिटल ग्रामपंचायत प्रवेश',
    description: 'नागरिक, ग्रामपंचायत कर्मचारी आणि व्यवसाय करदात्यांसाठी सुरक्षित प्रवेश.',
    badge: 'ग्रामपंचायत सेवा',
    icon: 'building',
  },
  taluka: {
    eyebrow: 'अधिकृत शासकीय प्रवेश',
    title: 'तालुका अधिकारी प्रवेश',
    description: 'तहसीलदार आणि उपविभागीय अधिकाऱ्यांसाठी स्वतंत्र सुरक्षित कार्यक्षेत्र.',
    badge: 'तालुकास्तरीय कार्यक्षेत्र',
    icon: 'landmark',
  },
  system: {
    eyebrow: 'मर्यादित प्रणाली प्रवेश',
    title: 'प्रणाली प्रशासक प्रवेश',
    description: 'प्रणाली संरचना, ग्रामपंचायत व्यवस्थापन आणि नियंत्रित स्थलांतरासाठी सुरक्षित प्रवेश.',
    badge: 'उच्च सुरक्षा कार्यक्षेत्र',
    icon: 'shield',
  },
};

function LoginIcon({ name, size = 20 }) {
  const common = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true };
  if (name === 'mail') return <svg {...common}><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></svg>;
  if (name === 'lock') return <svg {...common}><rect x="4" y="10" width="16" height="11" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /></svg>;
  if (name === 'phone') return <svg {...common}><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.12.9.33 1.78.62 2.63a2 2 0 0 1-.45 2.11L8 9.73a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.85.29 1.73.5 2.63.62A2 2 0 0 1 22 16.92Z" /></svg>;
  if (name === 'eye') return <svg {...common}><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" /><circle cx="12" cy="12" r="3" /></svg>;
  if (name === 'eye-off') return <svg {...common}><path d="m3 3 18 18" /><path d="M10.6 10.6A2 2 0 0 0 13.4 13.4" /><path d="M9.9 4.2A10 10 0 0 1 12 4c6.5 0 10 8 10 8a17 17 0 0 1-2.2 3.4M6.6 6.6C3.9 8.5 2 12 2 12s3.5 8 10 8a10 10 0 0 0 4.1-.8" /></svg>;
  if (name === 'shield') return <svg {...common}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" /><path d="m9 12 2 2 4-4" /></svg>;
  if (name === 'landmark') return <svg {...common}><path d="m3 10 9-6 9 6" /><path d="M5 10v8M9 10v8M15 10v8M19 10v8M3 21h18" /></svg>;
  if (name === 'check') return <svg {...common}><path d="m5 12 4 4L19 6" /></svg>;
  return <svg {...common}><path d="M3 21h18M5 21V8l7-4 7 4v13M9 21v-6h6v6M9 10h.01M12 10h.01M15 10h.01" /></svg>;
}

export default function LoginPage({ portal = 'gp' }) {
  const config = PORTALS[portal] || PORTALS.gp;
  const [searchParams] = useSearchParams();
  const gpFromUrl = portal === 'gp' ? searchParams.get('gp') || '' : '';
  const redirectParam = searchParams.get('redirect') || searchParams.get('returnTo') || '';
  const safeRedirect = redirectParam.startsWith('/') && !redirectParam.startsWith('//') ? redirectParam : '';
  const { tenants, tenantsLoading } = useTenant();
  const { login, loginWithOtp, requestOtp, isAuthenticated, user, tenant: authTenant } = useAuth();
  const navigate = useNavigate();

  const [tenantSlug, setTenantSlug] = useState(gpFromUrl);
  const [authMode, setAuthMode] = useState('password');
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [acceptedLegal, setAcceptedLegal] = useState(false);

  const activeTenants = useMemo(() => tenants.filter(item => item?.slug), [tenants]);
  const selectedTenant = useMemo(() => activeTenants.find(item => item.slug === tenantSlug) || null, [activeTenants, tenantSlug]);
  const multipleTenants = portal === 'gp' && activeTenants.length > 1;
  const hasTenant = portal !== 'gp' || Boolean(selectedTenant);
  const logoUrl = portal === 'gp' && !logoFailed ? selectedTenant?.logo_url : null;

  const citizenRedirect = useCallback(slug => {
    const fallback = slug ? `/gp/${slug}` : '/';
    if (!safeRedirect || !slug) return fallback;
    return safeRedirect === `/gp/${slug}` || safeRedirect.startsWith(`/gp/${slug}/`) ? safeRedirect : fallback;
  }, [safeRedirect]);

  useEffect(() => {
    if (portal !== 'gp' || tenantsLoading) return;
    setTenantSlug(currentSlug => {
      const requested = activeTenants.find(item => item.slug === gpFromUrl);
      if (requested) return requested.slug;
      if (activeTenants.length === 1) return activeTenants[0].slug;
      return activeTenants.some(item => item.slug === currentSlug) ? currentSlug : '';
    });
  }, [portal, tenantsLoading, activeTenants, gpFromUrl]);

  useEffect(() => {
    setLogoFailed(false);
    setOtpSent(false);
    setOtp('');
    setError('');
    setNotice('');
    setAcceptedLegal(false);
  }, [tenantSlug, portal]);

  useEffect(() => {
    if (!isAuthenticated || !user) return;
    const storedPortal = localStorage.getItem('portalType');
    const savedTenant = authTenant || safeJsonParse(localStorage.getItem('tenant'));
    if (storedPortal === 'system' || user.role === 'super_admin') navigate('/system/dashboard', { replace: true });
    else if (storedPortal === 'taluka' || ['tahashil_officer', 'sdo_officer'].includes(user.role)) navigate('/taluka/dashboard', { replace: true });
    else if (user.role === 'citizen') navigate(citizenRedirect(savedTenant?.slug), { replace: true });
    else if (savedTenant?.slug) navigate(`/admin/${savedTenant.slug}/dashboard`, { replace: true });
  }, [isAuthenticated, user, authTenant, navigate, citizenRedirect]);

  const redirectAfterLogin = result => {
    if (portal === 'system') return navigate('/system/dashboard');
    if (portal === 'taluka') return navigate('/taluka/dashboard');
    if (result.user.role === 'citizen') return navigate(citizenRedirect(result.tenant?.slug));
    if (result.tenant?.slug) return navigate(`/admin/${result.tenant.slug}/dashboard`);
    return navigate('/');
  };

  const friendlyError = requestError => {
    const approval = requestError.response?.data?.approval_status;
    const codeMessage = requestError.response?.data?.error || requestError.message;
    if (approval === 'pending') return 'तुमचे खाते पडताळणीसाठी प्रलंबित आहे. कृपया ग्रामपंचायत कार्यालयाच्या मंजुरीची प्रतीक्षा करा.';
    if (approval === 'rejected') return 'तुमची नोंदणी नाकारली आहे. अधिक माहितीसाठी ग्रामपंचायत कार्यालयाशी संपर्क करा.';
    if (codeMessage === 'WhatsApp OTP is not configured yet') return 'WhatsApp OTP सेवा सध्या उपलब्ध नाही. कृपया ईमेल/फोन आणि पासवर्ड वापरा.';
    if (/Invalid credentials/i.test(codeMessage || '')) return 'प्रवेश माहिती चुकीची आहे किंवा हे खाते या प्रवेश विभागासाठी अधिकृत नाही.';
    if (/OTP is invalid or expired/i.test(codeMessage || '')) return 'OTP चुकीचा आहे किंवा त्याची मुदत संपली आहे.';
    return codeMessage || 'लॉगिन पूर्ण होऊ शकले नाही. कृपया पुन्हा प्रयत्न करा.';
  };

  const changeMode = mode => {
    setAuthMode(mode);
    setError('');
    setNotice('');
    setOtpSent(false);
    setOtp('');
  };

  const handleSubmit = async event => {
    event.preventDefault();
    setError('');
    setNotice('');
    if (!hasTenant) return setError('कृपया ग्रामपंचायत निवडा.');
    if (!acceptedLegal) return setError('प्रवेश करण्यापूर्वी अटी व शर्ती आणि गोपनीयता धोरण स्वीकारणे आवश्यक आहे.');
    setLoading(true);
    try {
      if (authMode === 'otp') {
        if (!/^\d{10}$/.test(phone)) throw new Error('कृपया नोंदणीकृत १० अंकी WhatsApp क्रमांक भरा.');
        if (!otpSent) {
          await requestOtp(phone, portal === 'gp' ? tenantSlug : undefined, portal);
          setOtpSent(true);
          setNotice('नोंदणीकृत खाते असल्यास WhatsApp वर ६ अंकी OTP पाठवला आहे. OTP ५ मिनिटांसाठी वैध आहे.');
          return;
        }
        if (!/^\d{6}$/.test(otp)) throw new Error('कृपया ६ अंकी OTP भरा.');
        const result = await loginWithOtp(phone, otp, portal === 'gp' ? tenantSlug : undefined, portal);
        return redirectAfterLogin(result);
      }
      if (!identifier.trim() || !password) throw new Error('ईमेल/फोन आणि पासवर्ड भरा.');
      const result = await login(identifier.trim(), password, portal === 'gp' ? tenantSlug : undefined, portal);
      return redirectAfterLogin(result);
    } catch (requestError) {
      setError(friendlyError(requestError));
    } finally {
      setLoading(false);
    }
  };

  const registrationPath = selectedTenant ? `/gp/${selectedTenant.slug}/register` : '';
  const legalBase = selectedTenant ? `/gp/${selectedTenant.slug}/legal` : '/legal';
  const tenantName = selectedTenant?.name || 'डिजिटल ग्रामपंचायत पोर्टल';

  return <main className={`login-page login-page--${portal}`} style={{ '--login-bg-image': `url(${process.env.PUBLIC_URL || ''}/HeaderBgImages/17.png)` }}>
    <div className="login-shell">
      <aside className="login-story" aria-label="Portal information">
        <div className="login-story-top">
          <span className="login-security-pill"><LoginIcon name="shield" size={16} /> {config.badge}</span>
          <div className="login-brand-mark">
            {logoUrl ? <img src={logoUrl} alt={`${tenantName} चिन्ह`} onError={() => setLogoFailed(true)} /> : <LoginIcon name={config.icon} size={42} />}
          </div>
          <span className="login-story-eyebrow">{config.eyebrow}</span>
          <h1>{portal === 'gp' && selectedTenant ? selectedTenant.name : config.title}</h1>
          <p>{config.description}</p>
        </div>
        <div className="login-assurances">
          <div><span><LoginIcon name="check" size={15} /></span><p><strong>सुरक्षित प्रमाणीकरण</strong><small>पासवर्ड किंवा नोंदणीकृत WhatsApp क्रमांकाद्वारे प्रवेश</small></p></div>
          <div><span><LoginIcon name="check" size={15} /></span><p><strong>भूमिकेनुसार प्रवेश</strong><small>प्रत्येक खात्याला फक्त अधिकृत कार्यक्षेत्र उपलब्ध</small></p></div>
          <div><span><LoginIcon name="check" size={15} /></span><p><strong>गोपनीयता संरक्षित</strong><small>तुमची प्रवेश माहिती कोणासोबतही शेअर करू नका</small></p></div>
        </div>
        <p className="login-story-footer">महाराष्ट्रातील ग्रामपंचायत सेवांसाठी एकात्मिक डिजिटल व्यासपीठ</p>
      </aside>

      <section className="login-card" aria-label={`${config.title} अर्ज`}>
        <header className="login-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
            <span className="login-header-icon"><LoginIcon name={config.icon} size={22} /></span>
            <div><span>{config.eyebrow}</span><h2>{config.title}</h2><p>{portal === 'gp' ? 'तुमच्या खात्यात सुरक्षितपणे प्रवेश करा' : 'फक्त अधिकृत अधिकाऱ्यांसाठी'}</p></div>
          </div>
          <GoogleTranslate variant="light" />
        </header>

        {portal === 'gp' && !tenantsLoading && activeTenants.length === 0 && <div className="login-error">सध्या कोणतीही सक्रिय ग्रामपंचायत उपलब्ध नाही.</div>}
        {error && <div className="login-error" role="alert">{error}</div>}
        {notice && <div className="login-notice" role="status">{notice}</div>}

        <form onSubmit={handleSubmit} className="login-form" noValidate>
          {portal === 'gp' && multipleTenants && <div className="form-group">
            <label htmlFor="login-tenant">ग्रामपंचायत <span>*</span></label>
            <div className="login-input-wrap"><LoginIcon name="building" /><select id="login-tenant" value={tenantSlug} onChange={event => setTenantSlug(event.target.value)} required><option value="">ग्रामपंचायत निवडा</option>{activeTenants.map(tenant => <option key={tenant.id} value={tenant.slug}>{tenant.name}{tenant.district ? `, ${tenant.district}` : ''}</option>)}</select></div>
          </div>}

          {portal === 'gp' && !tenantsLoading && activeTenants.length === 1 && selectedTenant && <div className="login-tenant-lock"><span className="login-tenant-mini-logo">{logoUrl ? <img src={logoUrl} alt="" onError={() => setLogoFailed(true)} /> : <LoginIcon name="building" />}</span><div><small>निवडलेली ग्रामपंचायत</small><strong>{selectedTenant.name}</strong>{selectedTenant.taluka && <span>{selectedTenant.taluka}, {selectedTenant.district}</span>}</div><span className="login-lock-badge">सक्रिय</span></div>}

          <div className="login-auth-tabs" role="tablist" aria-label="प्रवेश पद्धत">
            <button type="button" role="tab" aria-selected={authMode === 'password'} className={authMode === 'password' ? 'active' : ''} onClick={() => changeMode('password')}><LoginIcon name="lock" size={17} /> पासवर्ड</button>
            <button type="button" role="tab" aria-selected={authMode === 'otp'} className={authMode === 'otp' ? 'active' : ''} onClick={() => changeMode('otp')}><LoginIcon name="phone" size={17} /> WhatsApp OTP</button>
          </div>

          {authMode === 'password' ? <>
            <div className="form-group"><label htmlFor="login-identifier">ईमेल किंवा फोन नंबर</label><div className="login-input-wrap"><LoginIcon name="mail" /><input id="login-identifier" className="login-english-input" lang="en" dir="ltr" inputMode="email" autoCapitalize="none" spellCheck={false} value={identifier} onChange={event => setIdentifier(normalizeEnglishIdentifierInput(event.target.value))} placeholder="ईमेल किंवा 10 अंकी फोन नंबर" autoComplete="username" required /></div></div>
            <div className="form-group"><div className="login-label-row"><label htmlFor="login-password">पासवर्ड</label><button type="button" onClick={() => setShowForgotModal(true)}>पासवर्ड विसरलात?</button></div><div className="login-input-wrap"><LoginIcon name="lock" /><input id="login-password" type={showPassword ? 'text' : 'password'} value={password} onChange={event => setPassword(event.target.value)} placeholder="तुमचा पासवर्ड" autoComplete="current-password" required /><button type="button" className="login-password-toggle" onClick={() => setShowPassword(value => !value)} aria-label={showPassword ? 'पासवर्ड लपवा' : 'पासवर्ड दाखवा'}><LoginIcon name={showPassword ? 'eye-off' : 'eye'} /></button></div></div>
          </> : <>
            <div className="form-group"><label htmlFor="login-phone">नोंदणीकृत WhatsApp क्रमांक</label><div className="login-input-wrap"><LoginIcon name="phone" /><input id="login-phone" className="login-english-input" lang="en" dir="ltr" type="tel" inputMode="numeric" value={phone} onChange={event => setPhone(normalizePhoneInput(event.target.value))} placeholder="10 अंकी फोन नंबर" autoComplete="tel" required /></div></div>
            {otpSent && <div className="form-group"><div className="login-label-row"><label htmlFor="login-otp">६ अंकी OTP</label><button type="button" onClick={() => { setOtpSent(false); setOtp(''); setNotice('नवीन OTP मागवण्यासाठी पुन्हा पाठवा निवडा.'); }}>क्रमांक बदला</button></div><div className="login-input-wrap login-otp-wrap"><LoginIcon name="shield" /><input id="login-otp" className="login-english-input" lang="en" dir="ltr" inputMode="numeric" value={otp} onChange={event => setOtp(normalizeEnglishDigitsInput(event.target.value, 6))} placeholder="••••••" autoComplete="one-time-code" required /></div></div>}
          </>}

          <label className="legal-check">
            <input type="checkbox" checked={acceptedLegal} onChange={event => setAcceptedLegal(event.target.checked)} required />
            <span>मी <Link to={`${legalBase}/terms`} target="_blank">अटी व शर्ती</Link> आणि <Link to={`${legalBase}/privacy`} target="_blank">गोपनीयता धोरण</Link> वाचले असून त्यांना सहमती देतो/देते. माझ्या अधिकृत वापराची सुरक्षा नोंद ठेवली जाऊ शकते.</span>
          </label>

          <button type="submit" className="btn-login" disabled={loading || tenantsLoading || !hasTenant || !acceptedLegal}><span>{loading ? 'कृपया प्रतीक्षा करा…' : authMode === 'otp' && !otpSent ? 'WhatsApp OTP पाठवा' : 'सुरक्षित प्रवेश करा'}</span><b>→</b></button>

          {portal === 'gp' && <div className="login-register-box"><span><LoginIcon name="shield" /></span><div><strong>नवीन नागरिक किंवा गावाबाहेरील व्यवसाय करदाता?</strong><p>व्यवसाय मालकाला गावातील नागरिक असणे आवश्यक नाही. GP पडताळणीनंतर व्यवसाय कर पाहण्यासाठी व भरण्यासाठी स्वतंत्र प्रवेश मिळतो.</p>{registrationPath ? <div className="login-register-actions"><Link to={registrationPath}>नागरिक खाते</Link><Link to={`${registrationPath}?type=business`}>व्यवसाय कर खाते →</Link></div> : <em>नोंदणीसाठी आधी ग्रामपंचायत निवडा</em>}</div></div>}
        </form>

        <footer className="login-footer"><Link to="/">← मुख्यपृष्ठावर जा</Link><span className="legal-consent-links"><Link to={`${legalBase}/terms`}>अटी</Link> · <Link to={`${legalBase}/privacy`}>गोपनीयता</Link> · <Link to={`${legalBase}/accessibility`}>सुलभता</Link></span><span>सहाय्य आवश्यक असल्यास अधिकृत कार्यालयाशी संपर्क करा</span></footer>
      </section>

      <ForgotPasswordModal
        isOpen={showForgotModal}
        onClose={() => setShowForgotModal(false)}
        portal={portal}
        tenantSlug={tenantSlug}
        onResetSuccess={(resetPhone) => {
          setIdentifier(resetPhone);
          changeMode('password');
          setNotice('✅ पासवर्ड यशस्वीरीत्या बदलला आहे. कृपया नवीन पासवर्डने लॉग इन करा.');
        }}
      />
    </div>
  </main>;
}

export { PORTALS };
